import {Controller} from "@hotwired/stimulus";
import {formatPrice} from "../utils";

export default class extends Controller {
  static targets = [
    "form",
    "dropdown",
    "quantity",
    "input",
    "quantityDisplay",
    "priceDisplay",
    "inputDisplay",
    "submitButton",
    "chevron",
    "backdrop",
  ];
  static values = {
    quantity: Number,
    currency: String,
    price: Number,
    caseQuantity: Number,
    packagingType: String,
  };

  toggleDropdown() {
    const isOpen = !this.dropdownTarget.classList.contains("hidden");

    this.dropdownTarget.classList.toggle("hidden", isOpen);
    this.backdropTarget.classList.toggle("hidden", isOpen);
    this.chevronTarget.classList.toggle("rotate-180", !isOpen);
  }

  select(e) {
    const quantity = e.params.quantity;

    this.setQuantity(quantity);
    this.updateDisplay(quantity);

    this.toggleDropdown();
  }

  activateInput() {
    this.inputDisplayTarget.classList.add("hidden");
    this.inputTarget.classList.remove("hidden");

    this.inputTarget.focus();
  }

  customInputHandler(e) {
    this.setQuantity(e.target.value);
  }

  customInputEnterHandler(e) {
    // Prevent navigation to product
    e.preventDefault();
    e.stopPropagation();

    this.customInputBlurHandler();
    this.submit();
  }

  customInputBlurHandler() {
    if (this.inputTarget.value === "") {
      this.inputTarget.classList.add("hidden");
      this.inputDisplayTarget.classList.remove("hidden");

      // Restore initial label
      this.inputDisplayTarget.innerText = this.inputDisplayTarget.dataset.initialText;
      return;
    } else if (!this.inputTarget.checkValidity()) {
      const currentValue = this.inputTarget.value;
      const ceilRoundedValue = Math.ceil(currentValue / this.caseQuantityValue) * this.caseQuantityValue;
      const floorRoundedValue = Math.max(1, Math.floor(currentValue / this.caseQuantityValue)) * this.caseQuantityValue;

      const validValue =
        Math.abs(currentValue - ceilRoundedValue) < Math.abs(currentValue - floorRoundedValue)
          ? ceilRoundedValue
          : floorRoundedValue;

      this.setQuantity(validValue);
      this.inputTarget.value = validValue;
    }

    this.updateDisplay(this.quantityTarget.value);

    this.inputTarget.classList.add("hidden");
    this.inputDisplayTarget.classList.remove("hidden");
    this.submitButtonTarget.removeAttribute("disabled");
  }

  submit() {
    this.formTarget.requestSubmit();
  }

  hideDropdown() {
    this.dropdownTarget.classList.add("hidden");
    this.backdropTarget.classList.add("hidden");
    this.chevronTarget.classList.remove("rotate-180");
  }

  renderInputDisplayContent(quantity) {
    const quantitySpan = document.createElement("span");
    const priceSpan = document.createElement("span");

    quantitySpan.className =
      "text-black !font-bold inline-block mr-1.5 transition-colors duration-200 group-hover/item:text-inherit";
    quantitySpan.innerText = quantity;

    priceSpan.className = "secondary-font group-hover/item:text-inherit";
    priceSpan.innerText = this.formattedPrice();

    if (this.inputDisplayTarget.dataset.bundle === "true") {
      const bundleLabel =
        quantity === 1 ? this.inputDisplayTarget.dataset.bundleSingular : this.inputDisplayTarget.dataset.bundlePlural;
      quantitySpan.innerText += ` ${bundleLabel}`;
    }

    this.inputDisplayTarget.innerHTML = "";
    this.inputDisplayTarget.appendChild(quantitySpan);
    this.inputDisplayTarget.appendChild(priceSpan);
  }

  setQuantity(value) {
    this.quantityTarget.value = value;

    // Manually trigger the input event for analytics
    this.quantityTarget.dispatchEvent(new Event("input"));
  }

  updateDisplay(quantity) {
    const priceText = this.formattedPrice();

    this.quantityDisplayTarget.innerText = quantity;
    this.priceDisplayTarget.innerText = priceText;

    if (!this.inputTarget.classList.contains("hidden")) {
      this.renderInputDisplayContent(quantity);
    }
  }

  formattedPrice() {
    return `(${formatPrice(this.priceValue, this.currencyValue)} / ${this.packagingTypeValue})`;
  }
}
